import { Box, Circle, Flex, Stack, Text } from '@chakra-ui/react'
import { css } from '@emotion/react'
import { DateTime } from 'luxon'
import { useRouter } from 'next/router'
import React from 'react'
import DayPicker from 'react-day-picker'
import 'react-day-picker/lib/style.css'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import interpolateColor from '@/utils/interpolateColor'
import logger from '@/utils/logger'

import UserEventQ from '@/queries/UserEventQ'
import UserQ from '@/queries/UserQ'
import { useAppContext } from '@/client/context/app'
import { useUser } from '@/client/context/user'
import { type Query, SQL } from '@/client/query'
import { EVENT_DASHBOARD_TITLE } from '@/client/services/progression'
import type { UserEventType } from '@/client/services/progression/types'

import PointsTag from '@/components/Achievements/PointsTag'
import ModalV3 from '@/components/UI/Modal/ModalV3'

const CALENDAR_START_DATE = DateTime.local(2021, 1, 1)

function getDateTimeKey(dateTime: DateTime, userTimezone: string): string {
  return dateTime.setZone(userTimezone).startOf('day').toISO()
}

type ActivityEvent = {
  type: UserEventType['type']
  points: number
  createdAt: DateTime
}

const QUERY_YEAR = 'year'
const QUERY_MONTH = 'month'

export default function ProfileActivityCalendar({ user }: { user: Query }): JSX.Element {
  const router = useRouter()
  const appContext = useAppContext()
  const now = DateTime.fromISO(appContext.requestTimestamp, { zone: 'utc' })

  const currentUser = useUser()

  const year = Number.parseInt(`${router.query[QUERY_YEAR]}`, 10) || now.get('year')
  const month = Number.parseInt(`${router.query[QUERY_MONTH]}`, 10) || now.get('month')

  const events = user.many(
    UserEventQ.where(
      {
        userId: UserQ.id,
        order: [[UserEventQ.created_at, 'asc']],
      },
      // Load a month before and after, so that clicking left / right is smooth
      SQL`${UserEventQ.created_at} >= MAKE_DATE(${year}, ${month}, 1) - INTERVAL '1 MONTH'
      AND ${UserEventQ.created_at} < MAKE_DATE(${year}, ${month}, 1) + INTERVAL '2 MONTH'`
    )
  )

  const handleMonthChange = React.useCallback(
    (date: Date) => {
      const dateTime = DateTime.fromJSDate(date)
      router
        .replace(
          {
            query: {
              ...router.query,
              [QUERY_YEAR]: dateTime.get('year'),
              [QUERY_MONTH]: dateTime.get('month'),
            },
          },
          undefined,
          { scroll: false }
        )
        .catch(err => {
          logger.error('Navigation failed', { err })
        })
    },
    [router]
  )

  const [eventsToShow, setEventsToShow] = React.useState<ActivityEvent[]>([])
  const [modalDay, setModalDay] = React.useState<DateTime | null>(null)
  const eventsByDay: Record<string, ActivityEvent[]> = {}

  events.forEach(event => {
    const points = event(UserEventQ.event_points) ?? 0
    const createdAt = DateTime.fromISO(`${event(UserEventQ.created_at)}`)
    const type = event(UserEventQ.event_type) as UserEventType['type']
    const key = getDateTimeKey(createdAt, currentUser.timezone)
    if (eventsByDay[key] == null) {
      eventsByDay[key] = []
    }
    eventsByDay[key].push({ points, createdAt, type })
  })

  return (
    <Box>
      <ModalV3
        open={modalDay != null}
        onClose={() => setModalDay(null)}
        title={`Activity on ${modalDay?.toFormat('LLLL d, yyyy')}`}
        body={() => (
          <Stack
            divider={<Box borderBottom={`2px solid ${chakraTokenToVar('border')}`} />}
            minWidth="330px"
          >
            {eventsToShow.map(({ points, createdAt, type }) => (
              <Stack
                key={`${type}-${createdAt.toISO()}`}
                spacing={2}
                justify="space-between"
                align="center"
                direction="row"
                py={1}
              >
                <Box>
                  <Text fontSize="16px" mb={1} fontWeight={500}>
                    {EVENT_DASHBOARD_TITLE[type]}
                  </Text>
                  <Text color="fg.muted">{createdAt.toFormat('h:mm a')}</Text>
                </Box>
                <Box>
                  <PointsTag points={points} />
                </Box>
              </Stack>
            ))}
          </Stack>
        )}
      />

      <Flex
        css={css`
          .DayPicker-Month {
            margin: 0;
          }
          .DayPicker-NavButton {
            top: 0.25em;
            right: 0.5em;
          }`}
        flex={0}
        minHeight="320px"
      >
        <DayPicker
          initialMonth={DateTime.local(year, month, 1).toJSDate()}
          fromMonth={CALENDAR_START_DATE.toJSDate()}
          toMonth={now.toJSDate()}
          onMonthChange={handleMonthChange}
          renderDay={date => {
            const dateTime = DateTime.fromJSDate(date)
            const dayNumber = dateTime.get('day')
            const key = getDateTimeKey(dateTime, currentUser.timezone)
            const eventsOnDay = eventsByDay[key] ?? []
            const numPoints = eventsOnDay.reduce((sum, current) => sum + current.points, 0)
            return numPoints === 0 ? (
              <Circle size={['30px', '30px', '23px', '20px', '30px']} color="fg" fontSize="14px">
                {dayNumber}
              </Circle>
            ) : (
              <Circle
                cursor="pointer"
                size={['30px', '30px', '23px', '20px', '30px']}
                fontSize="14px"
                bg={interpolateColor({
                  color1: 'yellow.muted',
                  color2: 'yellow.900',
                  ratio: Math.min(1, (numPoints * 1) / 20),
                })}
                color="fg"
                onClick={() => {
                  setModalDay(dateTime)
                  setEventsToShow(eventsOnDay)
                }}
              >
                {dayNumber}
              </Circle>
            )
          }}
        />
      </Flex>
    </Box>
  )
}
